import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import useTitle from "utils/custom-hooks/useTitle";
import theme from "theme/default";
import {
  borderRadius,
  fontWeight,
  fontSize,
  gradients,
  padding,
} from "theme/defaultStyle";
import GradientIcon from "theme/icons/GradientIcon";
import PremiumReceivablesIcon from "theme/icons/PremiumReceivablesIcon";
import TabNavigator from "controls/global/tab-navigator/TabNavigator";
import { TabNavigatorItem } from "entities/UIModel";
import ActionButton from "controls/global/action-button/ActionButton";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import TransactionsTab from "./transactions/TransactionsTab";
import ReceiptsTab from "./receipts/ReceiptsTab";
import LedgerReportTab from "./ledger-report/LedgerReportTab";
import UploadAccrualsAdjustmentsModal from "./modals/UploadAccrualsAdjustmentsModal";
import { usePremiumReceivableList } from "./usePremiumReceivableList";
import { useDebounced } from "utils/custom-hooks/useDebounced";
import { usePersistedRowsPerPage } from "utils/custom-hooks/usePersistedRowsPerPage";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { SAMPLE_AGENT_RECORDS } from "./sampleAgentsData";
import { SAMPLE_TRANSACTION_RECORDS } from "./sampleTransactionsData";
import { SAMPLE_RECEIPT_RECORDS } from "./sampleReceiptsData";

const Container = styled("div")({
  backgroundColor: theme.palette.background.default,
  borderBottomLeftRadius: borderRadius.xlarge,
  borderBottomRightRadius: borderRadius.xlarge,
  position: "relative",
  "& .header": {
    display: "flex",
    alignItems: "center",
  },
  "& .headerContainer": {
    display: "flex",
    alignItems: "center",
    height: "28px",
    margin: "40px 0px 20px 0px",
    justifyContent: "space-between",
  },
  "& .titleQuickReferenceCard": {
    background: gradients.redGradient03,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: fontWeight.bold2,
    fontSize: fontSize.xlarge2,
    paddingLeft: padding.small1,
  },
});

type PremiumReceivablesHomeProps = {
  saveFileRef: any;
  saveFileChangeRef: any;
  tabSaveChangesRef?: any;
};

const tabs: TabNavigatorItem[] = [
  { title: "Premium Receivables", disabled: false },
  { title: "Accounting Receivables", disabled: false },
  { title: "Ledger Report", disabled: false },
];

// Column-header search inputs feed this map keyed by PremiumReceivableCriteria
// field name. The SP exposes only scalar equality params (plus *From/*To for
// dates), so values are single, never arrays.
type HeaderFilters = Record<string, string | number | null>;

// When true, one unfiltered fetch fires as soon as the tab becomes active.
// Off here: nothing is loaded until the user clicks Apply Filters, matching
// Policy Management's History and Close Queue stages.
const ALLOW_INITIAL_LOAD_PR = false;

// Loader for the Premium Receivables tab. TabNavigator only mounts the active
// tab's child, so usePremiumReceivableList(...) is scoped to this tab — but it
// stays gated on `hasInitiatedSearch` until the user applies a filter.
const PremiumReceivablesTabLoader = ({
  userTimeZone,
}: {
  userTimeZone: string;
}) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("-TransactionDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [advancedFilters, setAdvancedFilters] = useState<HeaderFilters>({});
  // Seeded rather than set from a mount effect: an effect would flip `enabled`
  // one render after the criteria were already debounced, costing an extra fetch.
  const [hasInitiatedSearch, setHasInitiatedSearch] =
    useState<boolean>(ALLOW_INITIAL_LOAD_PR);

  const combined = useMemo(
    () => ({ ...appliedHeaderFilters, ...advancedFilters }),
    [appliedHeaderFilters, advancedFilters]
  );
  // `enabled` must flip in the SAME render as the criteria it belongs to,
  // otherwise Apply fires one throwaway request with stale criteria.
  const gate = useMemo(
    () => ({ criteria: combined, enabled: hasInitiatedSearch }),
    [combined, hasInitiatedSearch]
  );
  const debouncedGate = useDebounced(gate, 400);
  // Adjust-state-during-render (the documented React pattern): React re-renders
  // before committing, so the fetch effect only ever commits with page 1. As an
  // effect this ran AFTER the fetch had already gone out on the old page.
  const prevCriteriaRef = useRef(debouncedGate.criteria);
  if (prevCriteriaRef.current !== debouncedGate.criteria) {
    prevCriteriaRef.current = debouncedGate.criteria;
    setCurrentPage(1);
  }

  const { records, totalRows, totalPages, isLoading } = usePremiumReceivableList(
    pageSize,
    currentPage,
    sortColumn,
    debouncedGate.criteria,
    debouncedGate.enabled
  );

  const applyEnabled = useMemo(
    () =>
      Object.values(pendingHeaderFilters).some((v) => {
        if (v == null) return false;
        if (typeof v === "string") return v.trim() !== "";
        return true;
      }),
    [pendingHeaderFilters]
  );

  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };
  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };
  const handleFilterChange = useCallback(
    (name: string, value: string | number | null) => {
      // A cleared control reports null or "" — drop the key rather than parking
      // an empty value under it, so this map only ever holds live criteria and
      // Apply Filters can't be enabled by a leftover.
      setPendingHeaderFilters((prev) => {
        const next = { ...prev };
        if (value == null || (typeof value === "string" && value.trim() === "")) {
          delete next[name];
        } else {
          next[name] = value;
        }
        return next;
      });
    },
    []
  );
  // Back to the pre-search state rather than firing an unfiltered query, so
  // Apply Filters stays the only thing that can trigger a search. (History and
  // Close Queue re-search on Reset; this tab deliberately does not.)
  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setAdvancedFilters({});
    // Back to the seeded value (ALLOW_INITIAL_LOAD_PR), so the tab lands in the
    // same state as a fresh mount: empty grid, no request.
    setHasInitiatedSearch(false);
  }, []);
  const handleAdvancedSearchChange = useCallback((criteria: HeaderFilters) => {
    setAdvancedFilters(criteria);
    setHasInitiatedSearch(true);
  }, []);
  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setHasInitiatedSearch(true);
  }, [pendingHeaderFilters]);

  return (
    <TransactionsTab
      records={records}
      rowsPerPage={pageSize}
      onRowsPerPageChange={handlePageSizeChange}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={totalRows}
      totalPages={totalPages}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      // The debounced gate, not hasInitiatedSearch: it flips in step with the
      // fetch, so Apply doesn't flash "No results found" during the 400ms
      // debounce, and Reset's empty state lands with the cleared rows.
      hasSearched={debouncedGate.enabled}
      appliedCriteria={combined}
      userTimeZone={userTimeZone}
      onAdvancedSearchChange={handleAdvancedSearchChange}
    />
  );
};

const PremiumReceivablesHome = ({
  saveFileRef,
  saveFileChangeRef,
  tabSaveChangesRef,
}: PremiumReceivablesHomeProps) => {
  useTitle("Stewart Connect Policy Portal - Premium Receivables");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [{ profileSettings }] = useProfileSettingsCache();
  const userTimeZone = profileSettings?.userTimeZone ?? "";

  return (
    <Container>
      <Box className="headerContainer">
        <Box className="header">
          <GradientIcon Icon={PremiumReceivablesIcon} />
          <Typography
            className="titleQuickReferenceCard"
            variant="h6"
          >
            Premium Receivables
          </Typography>
        </Box>
        <ActionButton
          variant="contained"
          color="primary"
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Upload Accruals / Adjustments
        </ActionButton>
      </Box>

      <UploadAccrualsAdjustmentsModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      <TabNavigator
        tabs={tabs}
        tabSaveChangesRef={tabSaveChangesRef}
      >
        {[
          // Live data — the loader fires the API only when this tab is active.
          <PremiumReceivablesTabLoader
            key="premiumReceivables"
            userTimeZone={userTimeZone}
          />,
          <ReceiptsTab key="accountingReceivables" userTimeZone={userTimeZone} />,
          <LedgerReportTab
            key="ledgerReport"
            agents={SAMPLE_AGENT_RECORDS}
            transactions={SAMPLE_TRANSACTION_RECORDS}
            receipts={SAMPLE_RECEIPT_RECORDS}
          />,
        ]}
      </TabNavigator>
    </Container>
  );
};

export default PremiumReceivablesHome;
